import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

export interface AdminTenant {
  id: string
  name: string
  slug: string
  plan: string
  status: string
  admin_email: string | null
  admin_name: string | null
  created_at: string
  updated_at: string
  user_count: number
  last_login: string | null
}

interface AdminTenantListResponse {
  tenants: AdminTenant[]
  total: number
  limit: number
  offset: number
}

interface TenantActionResponse {
  id: string
  status: string
  message: string
}

interface UseAdminTenantsOptions {
  status?: string
  search?: string
  limit?: number
  offset?: number
}

/**
 * Fetch list of tenants for admin view
 */
export function useAdminTenants(options: UseAdminTenantsOptions = {}) {
  const { status, search, limit = 50, offset = 0 } = options

  return useQuery<AdminTenantListResponse>({
    queryKey: ['admin-tenants', { status, search, limit, offset }],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (status) params.append('status', status)
      if (search) params.append('search', search)
      params.append('limit', String(limit))
      params.append('offset', String(offset))

      const res = await api.get(`/admin/tenants?${params.toString()}`)
      return res.data
    },
  })
}

/**
 * Fetch single tenant details
 */
export function useAdminTenant(tenantId: string | null) {
  return useQuery<AdminTenant>({
    queryKey: ['admin-tenant', tenantId],
    queryFn: async () => {
      const res = await api.get(`/admin/tenants/${tenantId}`)
      return res.data
    },
    enabled: !!tenantId,
  })
}

/**
 * Approve a pending tenant
 */
export function useApproveTenant() {
  const queryClient = useQueryClient()

  return useMutation<TenantActionResponse, Error, string>({
    mutationFn: async (tenantId: string) => {
      const res = await api.post(`/admin/tenants/${tenantId}/approve`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
  })
}

/**
 * Reject a pending tenant
 */
export function useRejectTenant() {
  const queryClient = useQueryClient()

  return useMutation<TenantActionResponse, Error, string>({
    mutationFn: async (tenantId: string) => {
      const res = await api.post(`/admin/tenants/${tenantId}/reject`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
  })
}

/**
 * Suspend an active tenant
 */
export function useSuspendTenant() {
  const queryClient = useQueryClient()

  return useMutation<TenantActionResponse, Error, string>({
    mutationFn: async (tenantId: string) => {
      const res = await api.post(`/admin/tenants/${tenantId}/suspend`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
  })
}

/**
 * Reactivate a suspended tenant
 */
export function useReactivateTenant() {
  const queryClient = useQueryClient()

  return useMutation<TenantActionResponse, Error, string>({
    mutationFn: async (tenantId: string) => {
      const res = await api.post(`/admin/tenants/${tenantId}/reactivate`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
  })
}
